import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { formatDateTimeShort, formatIsoDateShort } from "@/lib/bookingDates";
import { bookingStatusLabel } from "@/lib/bookingStatus";
import { buildBookingsOpenUrl } from "@/lib/bookingNavigation";
import { formatCurrency, formatStayAmount } from "@/lib/money";
import { displayBookingNotes } from "@/lib/inquiryDisplay";
import { formatLicensePlate, vehicleTypeLabel } from "@/lib/vehicle";
import type { GuestStay, GuestStayList } from "@/types/camping";

function StayDetailGrid({ stay }: { stay: GuestStay }) {
  const rows: { label: string; value: string }[] = [
    {
      label: "Buchungsbetrag (netto)",
      value: formatStayAmount(stay.subtotal, stay.currency, stay.price_pending),
    },
    {
      label: `MwSt. (${stay.tax_rate_percent} %)`,
      value: formatStayAmount(stay.tax_amount, stay.currency, stay.price_pending),
    },
    {
      label: "Nachtpreis",
      value: formatStayAmount(stay.nightly_rate, stay.currency, stay.price_pending),
    },
    {
      label: "Rechnungen (ausgestellt)",
      value: formatCurrency(stay.billing.invoiced_total, stay.currency),
    },
    { label: "Bezahlt", value: formatCurrency(stay.billing.paid_amount, stay.currency) },
    { label: "Offen", value: formatCurrency(stay.billing.open_amount, stay.currency) },
    {
      label: "Personen",
      value: `${stay.adults} Erw., ${stay.children} Kind., ${stay.dogs} Hund(e)`,
    },
    { label: "Quelle", value: stay.source },
    {
      label: "Fahrzeug",
      value: stay.license_plate
        ? `${formatLicensePlate(stay.license_plate, stay.license_plate_country)}${stay.vehicle_type ? ` · ${vehicleTypeLabel(stay.vehicle_type)}` : ""}`
        : "—",
    },
    {
      label: "Strom",
      value: stay.wants_electricity
        ? stay.electricity_kwh
          ? `Ja (${stay.electricity_kwh} kWh)`
          : "Ja"
        : "Nein",
    },
    { label: "Transponder", value: stay.transponder_id ?? "—" },
    { label: "Schlüssel", value: stay.key_number ?? "—" },
    { label: "Externe Referenz", value: stay.external_reference ?? "—" },
  ];
  if (stay.actual_arrival_at) {
    rows.push({
      label: "Tatsächliche Anreise",
      value: formatDateTimeShort(stay.actual_arrival_at),
    });
  }
  if (stay.actual_departure_at) {
    rows.push({
      label: "Tatsächliche Abreise",
      value: formatDateTimeShort(stay.actual_departure_at),
    });
  }
  const bookingMessage = displayBookingNotes(stay.notes);
  if (bookingMessage) {
    rows.push({ label: "Nachricht", value: bookingMessage });
  }

  return (
    <dl className="mt-3 grid gap-2 border-t border-separator pt-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((row) => (
        <div key={row.label}>
          <dt className="text-xs text-label-tertiary">{row.label}</dt>
          <dd className="text-sm text-label">{row.value}</dd>
        </div>
      ))}
      {stay.billing.invoice_count > 0 ? (
        <div>
          <dt className="text-xs text-label-tertiary">Rechnungen</dt>
          <dd className="text-sm text-label">
            {stay.billing.invoice_count}
            {stay.billing.open_invoice_count > 0
              ? ` (${stay.billing.open_invoice_count} offen)`
              : null}
          </dd>
        </div>
      ) : null}
    </dl>
  );
}

function SummaryCards({ data }: { data: GuestStayList }) {
  const { summary } = data;
  const cards = [
    { label: "Aufenthalte", value: String(summary.total_stays) },
    { label: "Nächte gesamt", value: String(summary.total_nights) },
    {
      label: "Umsatz (Buchung)",
      value: formatCurrency(summary.booking_revenue_total, summary.currency),
    },
    {
      label: "Rechnungen",
      value: formatCurrency(summary.invoiced_total, summary.currency),
    },
    {
      label: "Bezahlt",
      value: formatCurrency(summary.paid_total, summary.currency),
    },
    {
      label: "Offen",
      value: formatCurrency(summary.open_total, summary.currency),
    },
  ];

  return (
    <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {cards.map((card) => (
        <div
          key={card.label}
          className="rounded-lg border border-separator bg-surface-primary/60 px-3 py-2"
        >
          <p className="text-xs text-label-tertiary">{card.label}</p>
          <p className="text-sm font-medium tabular-nums text-label">{card.value}</p>
        </div>
      ))}
    </div>
  );
}

export function GuestStayHistory({
  guestId,
  active,
}: {
  guestId: string;
  active: boolean;
}) {
  const navigate = useNavigate();
  const [data, setData] = useState<GuestStayList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!guestId) return;
    setLoading(true);
    setError(null);
    api
      .get<GuestStayList>(`/api/v1/guests/${guestId}/stays?limit=100`)
      .then((res) => {
        setData(res);
        setExpandedId(null);
      })
      .catch((err: unknown) => {
        setData(null);
        setError(
          err instanceof Error ? err.message : "Aufenthaltshistorie konnte nicht geladen werden.",
        );
      })
      .finally(() => setLoading(false));
  }, [guestId]);

  useEffect(() => {
    if (!active || !guestId) return;
    load();
  }, [active, guestId, load]);

  if (!active) return null;

  return (
    <section className="rounded-xl border border-separator bg-surface-secondary/40 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-medium text-label">Aufenthaltshistorie</h3>
        {data ? (
          <span className="text-sm text-label-secondary">
            {data.total} Aufenthalt{data.total === 1 ? "" : "e"}
          </span>
        ) : null}
      </div>

      {loading ? (
        <p className="mt-3 text-sm text-label-secondary">Aufenthalte werden geladen…</p>
      ) : null}

      {error ? <p className="mt-3 text-sm text-status-red">{error}</p> : null}

      {data ? <SummaryCards data={data} /> : null}

      {!loading && !error && data && data.items.length === 0 ? (
        <p className="mt-3 text-sm text-label-secondary">Noch keine Aufenthalte erfasst.</p>
      ) : null}

      {!loading && !error && data && data.items.length > 0 ? (
        <div className="mt-4 space-y-2">
          {data.items.map((stay) => {
            const expanded = expandedId === stay.id;
            return (
              <article
                key={stay.id}
                className="rounded-lg border border-separator bg-surface-primary/40"
              >
                <button
                  type="button"
                  className="flex w-full flex-col gap-2 p-3 text-left sm:flex-row sm:items-center sm:justify-between"
                  onClick={() => setExpandedId(expanded ? null : stay.id)}
                  aria-expanded={expanded}
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium tabular-nums">{stay.booking_number}</span>
                      <span className="text-sm text-label-secondary">
                        {bookingStatusLabel(stay.status)}
                      </span>
                    </div>
                    <p className="text-sm text-label-secondary">
                      {formatIsoDateShort(stay.arrival_date)} –{" "}
                      {formatIsoDateShort(stay.departure_date)}
                      {stay.pitch_code ? ` · Platz ${stay.pitch_code}` : null}
                      {stay.pitch_category_name ? ` (${stay.pitch_category_name})` : null}
                    </p>
                    <p className="text-xs text-label-tertiary">
                      {stay.nights} Nächte · {stay.adults}+{stay.children} Pers.
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1 sm:pl-4">
                    <span className="text-sm font-medium tabular-nums">
                      {formatStayAmount(stay.total_amount, stay.currency, stay.price_pending)}
                    </span>
                    {stay.billing.paid_amount !== "0" && stay.billing.paid_amount !== "0.00" ? (
                      <span className="text-xs text-label-tertiary">
                        bezahlt {formatCurrency(stay.billing.paid_amount, stay.currency)}
                      </span>
                    ) : null}
                  </div>
                </button>
                {expanded ? (
                  <div className="border-t border-separator px-3 pb-3">
                    <StayDetailGrid stay={stay} />
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        className="text-sm font-medium text-accent hover:underline"
                        onClick={() =>
                          navigate(buildBookingsOpenUrl(stay.id, stay.status))
                        }
                      >
                        Buchung öffnen →
                      </button>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
          {data.total > data.items.length ? (
            <p className="text-xs text-label-tertiary">
              Es werden die letzten {data.items.length} von {data.total} Aufenthalten angezeigt.
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
